import { Icons } from '~/features/stake/assets/icons'
import { useBalance } from '../api'

/**
 * The hover panel behind the BR1 Points figure in the menu bar, sibling to `FundsBreakdown`.
 *
 * Points had a native `title='Points'` tooltip, which named the number and said nothing else — not
 * where they come from, not what they are for, and not that the figure is a link. Those are the
 * three things the bar cannot say on its own, and this is the place people already point at.
 *
 * The unit is **BR1 Points**: `INVENTORY_PAGE.md` fixes that as the name the icon, the store, the
 * balance widget and `RewardSummary` all use. "Rewards" stays prose-only.
 *
 * Rendered on hover and on keyboard focus, and hidden from pointer events so it can never sit
 * between the cursor and the button underneath it.
 */
export const PointsBreakdown = () => {
    const { data: balance, isFetched } = useBalance()
    if (!isFetched) return null

    const points = balance?.point ?? 0
    const hasPoints = points > 0

    return (
        /*
          Anchored `left-0`, where the funds panel is `right-0`. On desktop the bar reverses into
          points-then-funds, so points is the left end of the money cluster and a right-anchored
          panel would hang back across the nav links.
        */
        <div className='absolute top-full left-0 pt-3 z-50 hidden md:group-hover/points:block md:group-focus-within/points:block pointer-events-none'>
            <div className='w-64 bg-black border border-white/20 shadow-2xl shadow-black p-4 flex flex-col gap-3 text-left font-normal'>
                <div className='text-2xs uppercase tracking-[0.2em] text-white/40'>BR1 Points</div>

                <div className='flex flex-col gap-1'>
                    <div className='flex flex-row items-center justify-between gap-3'>
                        <span className='inline-flex flex-row items-center gap-1.5 text-base font-bold whitespace-nowrap'>
                            <Icons.SkullPoint className='h-[1.2em] w-[1.2em]' />
                            {points.toFixed(2)}
                        </span>
                        <span className='text-2xs uppercase tracking-wide text-white/50'>Earned</span>
                    </div>
                    {/*
                      The loop in one sentence, in the order the user travels it: earn by locking,
                      spend in the store. `INVENTORY_PAGE.md` states it the same way round — "lock
                      assets to accrue BR1 Points… then spent in the Store".
                    */}
                    <p className='text-xs text-white/50'>
                        {hasPoints
                            ? 'Earned by locking assets in Inventory. Spend them in the Store.'
                            : 'None yet. Lock assets in Inventory to start earning them.'}
                    </p>
                </div>

                <div className='border-t border-white/10' />
                {/*
                  Says where the click goes, because the two destinations differ and a figure that
                  navigates somewhere unannounced is worse than one that does nothing.
                */}
                <div className='text-2xs uppercase tracking-wide text-white/40'>
                    {hasPoints ? 'Click to spend in the Store' : 'Click to start earning in Inventory'}
                </div>
            </div>
        </div>
    )
}
